import React, { useCallback, useMemo, useRef, useState } from 'react';
import TutorialMarkdown from './TutorialMarkdown';
import TutorialImageToolbar from './TutorialImageToolbar';
import {
  getPreviousImageLineIndex,
  joinImageWithPrevious,
  parseImageLine,
  setImageStandalone,
  updateImageBlock,
  validateImageRowWidth,
} from '@/pages/tutorial/utils/tutorialMarkdownImages';

export default function TutorialEditorPreview({ content, onChange }) {
  const [selectedImageLineIndex, setSelectedImageLineIndex] = useState(null);
  const lastMetaRef = useRef(null);
  const previewContentRef = useRef(null);

  const selectedMeta = useMemo(() => {
    if (selectedImageLineIndex == null) return null;
    const lines = String(content || '').replace(/\r\n/g, '\n').split('\n');
    const line = lines[selectedImageLineIndex];
    if (!line) return null;
    return parseImageLine(line.trim());
  }, [content, selectedImageLineIndex]);

  const hasPreviousImage = useMemo(() => {
    if (selectedImageLineIndex == null) return false;
    return getPreviousImageLineIndex(content, selectedImageLineIndex) != null;
  }, [content, selectedImageLineIndex]);

  const getContainerWidth = useCallback(() => {
    const el = previewContentRef.current;
    if (!el) return 800;
    return el.clientWidth || 800;
  }, []);

  if (selectedMeta) {
    lastMetaRef.current = selectedMeta;
  }

  const toolbarMeta = selectedMeta || lastMetaRef.current;

  const handleImageMetaChange = useCallback((lineIndex, meta) => {
    if (!onChange) return;
    const nextContent = updateImageBlock(content, lineIndex, meta);
    const validation = validateImageRowWidth(nextContent, lineIndex, getContainerWidth());
    if (!validation.ok) {
      window.alert(validation.message);
      return;
    }
    lastMetaRef.current = meta;
    setSelectedImageLineIndex(lineIndex);
    onChange(nextContent);
  }, [content, getContainerWidth, onChange]);

  const handleJoinPrevious = useCallback(() => {
    if (!onChange || selectedImageLineIndex == null) return;
    const result = joinImageWithPrevious(content, selectedImageLineIndex, getContainerWidth());
    if (!result.ok) {
      window.alert(result.message);
      return;
    }
    onChange(result.content);
    setSelectedImageLineIndex(selectedImageLineIndex);
  }, [content, getContainerWidth, onChange, selectedImageLineIndex]);

  const handleSetStandalone = useCallback(() => {
    if (!onChange || selectedImageLineIndex == null) return;
    const result = setImageStandalone(content, selectedImageLineIndex);
    if (!result.ok) {
      window.alert(result.message);
      return;
    }
    onChange(result.content);
    setSelectedImageLineIndex(selectedImageLineIndex);
  }, [content, onChange, selectedImageLineIndex]);

  const handleSelectImage = useCallback((lineIndex) => {
    setSelectedImageLineIndex(lineIndex);
    if (lineIndex == null) {
      lastMetaRef.current = null;
    }
  }, []);

  const handleCloseToolbar = useCallback(() => {
    setSelectedImageLineIndex(null);
    lastMetaRef.current = null;
  }, []);

  return (
    <div className="tutorial-editor-preview">
      <div className="tutorial-editor-preview__content" ref={previewContentRef}>
        <TutorialMarkdown
          content={content}
          interactiveImages
          selectedImageLineIndex={selectedImageLineIndex}
          onSelectImage={handleSelectImage}
        />
      </div>
      {selectedImageLineIndex != null && toolbarMeta ? (
        <TutorialImageToolbar
          meta={toolbarMeta}
          lineIndex={selectedImageLineIndex}
          hasPreviousImage={hasPreviousImage}
          onMetaChange={handleImageMetaChange}
          onJoinPrevious={handleJoinPrevious}
          onSetStandalone={handleSetStandalone}
          onClose={handleCloseToolbar}
        />
      ) : null}
    </div>
  );
}
